// /**
//  * Reusable deterministic progress timer.
//  *
//  * Common use-case: show "loading" progress that advances to a cap (default 99)
//  * while an async request is active, then you can set active=false to hide UI.
//  *
//  * Props:
//  * - active: boolean (start/stop timer)
//  * - durationMs: total time to reach maxValue (approx)
//  * - tickMs: interval for updates
//  * - maxValue: progress cap while active (commonly 95)
//  * - startValue: starting progress value when (re)starting
//  * - resetOnStart: reset progress to startValue whenever active turns true
//  *
//  * Returns:
//  * - progress: number
//  * - setProgress: setter (if you want to force 100, etc.)
//  * - reset: function to reset progress to startValue
//  */
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';

export default function useProgressTimer({
  active,
  durationMs = 4000,
  tickMs = 50,
  maxValue = 95,
  startValue = 0,
  resetOnStart = true,
} = {}) {

  const [progress, setProgress] = useState(startValue);

  const [visible, setVisible] = useState(false);

  const timerRef = useRef(null);

  const previousActive = useRef(false);

  const step = useMemo(() => {

    const totalTicks = Math.max(
      1,
      Math.ceil(durationMs / tickMs)
    );

    return maxValue / totalTicks;

  }, [durationMs, tickMs, maxValue]);

  const reset = useCallback(() => {
    setProgress(startValue);
  }, [startValue]);

  useEffect(() => {

    // START
    if (active) {

      previousActive.current = true;

      setVisible(true);

      if (resetOnStart) {
        setProgress(startValue);
      }

      clearInterval(timerRef.current);

      timerRef.current = setInterval(() => {

        setProgress((prev) => {

          let dynamicStep = step;

          // realistic slowdown
          if (prev >= 70) {
            dynamicStep = step / 2;
          }

          if (prev >= 85) {
            dynamicStep = step / 4;
          }

          const next = prev + dynamicStep;

          if (next >= maxValue) {

            clearInterval(timerRef.current);

            return maxValue;
          }

          return next;

        });

      }, tickMs);
    }

    // COMPLETE TO 100
    if (
      previousActive.current &&
      !active
    ) {

      clearInterval(timerRef.current);

      // FORCE 100%
      setProgress(100);

      const timeout = setTimeout(() => {

        setVisible(false);

        previousActive.current = false;

      }, 500);

      return () => clearTimeout(timeout);
    }

    return () => {
      clearInterval(timerRef.current);
    };

  }, [
    active,
    maxValue,
    resetOnStart,
    startValue,
    step,
    tickMs,
  ]);

  return {
    progress,
    visible,
    reset,
  };
}